import React, { useCallback, useEffect, useRef, useMemo, useState } from "react";
import api from "../api/client";
import { usePublishScreenData } from "../utils/aiScreenContext";
import ActionButton from "../components/ActionButton";
import { formatDateTime } from "../utils/datetime";

type Backup = { name: string; size_bytes: number; created_at: number };

function fmtSize(b: number): string {
  if (b >= 1024 * 1024 * 1024) return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
  if (b >= 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  if (b >= 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${b} B`;
}

export default function BackupRestore() {
  const [backups, setBackups] = useState<Backup[]>([]);
  const [backupDir, setBackupDir] = useState("");
  const [restoreSupported, setRestoreSupported] = useState(true);
  const [autoRestart, setAutoRestart] = useState(false);
  const [pending, setPending] = useState<{ backup_name?: string } | null>(null);
  const [loading, setLoading] = useState(true);

  // Publish the visible rows so the global AI Assist can answer
  // "what's on my screen". Projected explicitly: this payload goes
  // into a model prompt.
  usePublishScreenData(
    "Appliance backups",
    useMemo(
      () =>
        backups.map((b: any) => ({
          filename: b.filename ?? b.name ?? null,
          size_bytes: b.size_bytes ?? null,
          created_at: b.created_at ?? null,
        })),
      [backups],
    ),
    undefined,
  );
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [label, setLabel] = useState("");
  const [destination, setDestination] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const uploadRef = useRef<HTMLInputElement>(null);

  // Automatic backup schedule
  const [sched, setSched] = useState({
    enabled: false,
    cadence: "daily",
    hour: 2,
    day_of_week: 6,
    destination: "",
    retention_count: 14,
    last_run_at: null as string | null,
  });
  const [schedSaving, setSchedSaving] = useState(false);
  const [schedMsg, setSchedMsg] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    api
      .get("/api/v1/backup")
      .then((r) => {
        setBackups(r.data.backups || []);
        setBackupDir(r.data.backup_dir || "");
        setRestoreSupported(r.data.restore_supported !== false);
        setAutoRestart(!!r.data.auto_restart);
        setPending(r.data.pending_restore || null);
      })
      .catch((e) => setErr(e.response?.data?.detail || "Failed to load backups"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    api
      .get("/api/v1/backup/schedule")
      .then((r) => setSched((s) => ({ ...s, ...r.data })))
      .catch(() => {});
  }, [load]);

  const saveSchedule = async () => {
    setSchedSaving(true);
    setSchedMsg("");
    try {
      const r = await api.put("/api/v1/backup/schedule", {
        enabled: sched.enabled,
        cadence: sched.cadence,
        hour: sched.hour,
        day_of_week: sched.day_of_week,
        destination: sched.destination.trim() || null,
        retention_count: sched.retention_count,
      });
      setSched((s) => ({ ...s, ...r.data }));
      setSchedMsg("Schedule saved.");
    } catch (e: any) {
      setSchedMsg(e.response?.data?.detail || "Could not save schedule");
    } finally {
      setSchedSaving(false);
    }
  };

  const createBackup = async () => {
    setCreating(true);
    setMsg("");
    setErr("");
    try {
      const r = await api.post("/api/v1/backup", {
        label: label.trim() || null,
        destination: destination.trim() || null,
      });
      const where = r.data.off_box && r.data.path ? ` → saved to ${r.data.path}` : "";
      setMsg(`Backup created: ${r.data.name} (${fmtSize(r.data.size_bytes)})${where}.`);
      setLabel("");
      load();
    } catch (e: any) {
      setErr(e.response?.data?.detail || "Backup failed");
    } finally {
      setCreating(false);
    }
  };

  const download = (name: string) => {
    // Stream via the authenticated api base; open in a new tab with the token.
    api
      .get(`/api/v1/backup/${encodeURIComponent(name)}/download`, { responseType: "blob" })
      .then((r) => {
        const url = window.URL.createObjectURL(new Blob([r.data]));
        const a = document.createElement("a");
        a.href = url;
        a.download = name;
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => setErr("Download failed"));
  };

  const del = async (name: string) => {
    if (!window.confirm(`Delete backup ${name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/api/v1/backup/${encodeURIComponent(name)}`);
      load();
    } catch (e: any) {
      setErr(e.response?.data?.detail || "Delete failed");
    }
  };

  const restore = async (name: string) => {
    if (
      !window.confirm(
        `Restore the database from "${name}"?\n\n` +
          `This REPLACES all current data with the contents of this backup. Any changes ` +
          `made since it was taken will be lost. The system will restart to apply it.\n\n` +
          `A safety copy of the current database is saved automatically first. Continue?`,
      )
    )
      return;
    setRestoring(true);
    setMsg("");
    setErr("");
    try {
      const r = await api.post(`/api/v1/backup/${encodeURIComponent(name)}/restore`, {
        confirm: true,
      });
      setMsg(r.data.message || "Restore staged.");
      setPending({ backup_name: name });
    } catch (e: any) {
      setErr(e.response?.data?.detail || "Restore failed");
    } finally {
      setRestoring(false);
    }
  };

  const cancelRestore = async () => {
    try {
      await api.post("/api/v1/backup/cancel-restore");
      setPending(null);
      setMsg("Staged restore cancelled.");
    } catch (e: any) {
      setErr(e.response?.data?.detail || "Could not cancel");
    }
  };

  const upload = async (f: File) => {
    setMsg("");
    setErr("");
    const form = new FormData();
    form.append("file", f);
    try {
      const r = await api.post("/api/v1/backup/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMsg(`Uploaded ${r.data.name} (${fmtSize(r.data.size_bytes)}).`);
      load();
    } catch (e: any) {
      setErr(e.response?.data?.detail || "Upload failed (must be a .sqlite.gz / .sql.gz backup)");
    }
    if (uploadRef.current) uploadRef.current.value = "";
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Backup &amp; Restore</h1>
        <button
          onClick={load}
          className="text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-3 py-1.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/40"
        >
          ↻ Refresh
        </button>
      </div>

      {msg && (
        <p className="mb-4 text-sm text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg px-4 py-2">
          {msg}
        </p>
      )}
      {err && (
        <p className="mb-4 text-sm text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-4 py-2">
          {err}
        </p>
      )}

      {/* Create + upload */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5 mb-6">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
          Create a backup
        </h2>
        <div className="flex flex-wrap items-center gap-3">
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Optional label (e.g. pre-upgrade)"
            className="border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 text-sm w-56"
          />
          <input
            value={destination}
            onChange={(e) => setDestination(e.target.value)}
            placeholder={`Save to (blank = ${backupDir || "default folder"})`}
            title="Any location the AeroGauge server can write to — a local folder or a network share, e.g. \\\\fileserver\\backups"
            className="border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 text-sm w-80 font-mono"
          />
          <button
            onClick={createBackup}
            disabled={creating}
            className="bg-blue-600 text-white text-sm px-4 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {creating ? "Backing up… (up to a minute)" : "Back up now"}
          </button>
          <span className="text-gray-300 dark:text-gray-600">|</span>
          <input
            ref={uploadRef}
            type="file"
            accept=".gz"
            className="hidden"
            onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])}
          />
          <button
            onClick={() => uploadRef.current?.click()}
            className="text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-4 py-1.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/40"
          >
            Upload a backup
          </button>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
          Save to any location the AeroGauge server can reach — a local folder or a network share
          (e.g. <code className="font-mono">\\fileserver\backups</code>). Backups written outside
          the default folder won't appear in the list below.
        </p>
      </div>

      {/* Automatic backup schedule */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5 mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            Automatic backups
          </h2>
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
            <input
              type="checkbox"
              checked={sched.enabled}
              onChange={(e) => setSched({ ...sched, enabled: e.target.checked })}
            />
            Enabled
          </label>
        </div>
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-[11px] uppercase text-gray-500 dark:text-gray-400 mb-1">
              Frequency
            </label>
            <select
              value={sched.cadence}
              onChange={(e) => setSched({ ...sched, cadence: e.target.value })}
              className="border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-2 py-1.5 text-sm"
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
            </select>
          </div>
          {sched.cadence === "weekly" && (
            <div>
              <label className="block text-[11px] uppercase text-gray-500 dark:text-gray-400 mb-1">
                Day
              </label>
              <select
                value={sched.day_of_week}
                onChange={(e) => setSched({ ...sched, day_of_week: Number(e.target.value) })}
                className="border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-2 py-1.5 text-sm"
              >
                {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d, i) => (
                  <option key={i} value={i}>
                    {d}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="block text-[11px] uppercase text-gray-500 dark:text-gray-400 mb-1">
              At (hour, server time)
            </label>
            <select
              value={sched.hour}
              onChange={(e) => setSched({ ...sched, hour: Number(e.target.value) })}
              className="border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-2 py-1.5 text-sm"
            >
              {Array.from({ length: 24 }, (_, h) => (
                <option key={h} value={h}>
                  {String(h).padStart(2, "0")}:00
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[11px] uppercase text-gray-500 dark:text-gray-400 mb-1">
              Keep newest
            </label>
            <input
              type="number"
              min={0}
              value={sched.retention_count}
              onChange={(e) => setSched({ ...sched, retention_count: Number(e.target.value) })}
              className="border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-2 py-1.5 text-sm w-24"
              title="How many automatic backups to keep (0 = keep all). Manual backups are never pruned."
            />
          </div>
          <div className="flex-1 min-w-[16rem]">
            <label className="block text-[11px] uppercase text-gray-500 dark:text-gray-400 mb-1">
              Save to (blank = default folder)
            </label>
            <input
              value={sched.destination}
              onChange={(e) => setSched({ ...sched, destination: e.target.value })}
              placeholder="\\fileserver\backups or D:\Backups"
              className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 text-sm font-mono"
            />
          </div>
          <button
            onClick={saveSchedule}
            disabled={schedSaving}
            className="bg-blue-600 text-white text-sm px-4 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {schedSaving ? "Saving…" : "Save schedule"}
          </button>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
          {sched.last_run_at
            ? `Last automatic backup: ${formatDateTime(sched.last_run_at)}. `
            : "No automatic backup has run yet. "}
          Retention only prunes automatic backups — your manual backups are never deleted.
        </p>
        {schedMsg && (
          <p className="text-xs text-green-700 dark:text-green-400 mt-1">{schedMsg}</p>
        )}
      </div>

      {/* Backups list */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden mb-6">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900/50 border-b dark:border-gray-700">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Backup
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Size
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Created
              </th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {loading ? (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-gray-400">
                  Loading…
                </td>
              </tr>
            ) : backups.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-gray-400">
                  No backups yet — click "Back up now".
                </td>
              </tr>
            ) : (
              backups.map((b) => (
                <tr key={b.name} className="text-gray-800 dark:text-gray-100">
                  <td className="px-4 py-2 font-mono text-xs">{b.name}</td>
                  <td className="px-4 py-2">{fmtSize(b.size_bytes)}</td>
                  <td className="px-4 py-2">{new Date(b.created_at * 1000).toLocaleString()}</td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    <button
                      onClick={() => download(b.name)}
                      className="text-xs text-blue-600 hover:underline mr-3"
                    >
                      Download
                    </button>
                    {restoreSupported && (
                      <button
                        onClick={() => restore(b.name)}
                        disabled={restoring || !!pending}
                        className="text-xs text-amber-700 dark:text-amber-400 hover:underline mr-3 disabled:opacity-50"
                        title="Restore the database from this backup (replaces current data; restarts the app)"
                      >
                        Restore
                      </button>
                    )}
                    <button
                      onClick={() => del(b.name)}
                      className="text-xs text-red-600 hover:underline"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pending restore banner */}
      {pending && (
        <div className="bg-amber-100 dark:bg-amber-900/30 border border-amber-300 dark:border-amber-700 rounded-xl p-4 mb-6 text-sm text-amber-900 dark:text-amber-200 flex items-center justify-between gap-3">
          <span>
            ⏳ A restore{pending.backup_name ? ` from "${pending.backup_name}"` : ""} is staged.
            {autoRestart
              ? " The system is restarting to apply it — reload this page in about a minute."
              : " It will be applied the next time the application restarts."}
          </span>
          <ActionButton onAction={() => cancelRestore()} variant="raw" className="text-xs border border-amber-400 dark:border-amber-600 px-3 py-1 rounded-lg hover:bg-amber-200/60 dark:hover:bg-amber-800/40 whitespace-nowrap" pendingLabel="Restoring…">
            Cancel staged restore
          </ActionButton>
        </div>
      )}

      {/* How restore works */}
      <div className="bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 rounded-xl p-5 text-sm text-gray-700 dark:text-gray-300">
        <h2 className="font-semibold mb-2 text-gray-800 dark:text-gray-200">About restore</h2>
        {restoreSupported ? (
          <p className="mb-2">
            Click <strong>Restore</strong> on any backup (or upload one and restore it). The backup
            is staged and applied automatically when the application restarts — the running
            database is never overwritten in place, so the restore is safe. A safety copy of the
            current database is saved before the swap.
            {autoRestart
              ? " This deployment restarts itself automatically after you confirm."
              : " After confirming, restart the application to apply the restore."}
          </p>
        ) : (
          <p className="mb-2">
            This deployment uses PostgreSQL. Restore it with your standard database tooling
            (<code>pg_restore</code> into a fresh database) using a downloaded backup — the
            one-click restore here is available on SQLite deployments.
          </p>
        )}
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Note: a database backup does <strong>not</strong> include encryption master keys (needed
          to decrypt stored credentials) or uploaded files — keep those backed up separately as
          part of your deployment's disaster-recovery plan.
        </p>
      </div>
    </div>
  );
}
